<template>
  <div class="public-object-details">
    <div class="back-button-row">
      <router-link
        v-slot="{ navigate }"
        custom
        :to="{ name: 'PublicObjectList', query: $route.query }">
        <div
          class="btn btn-bordered basic"
          @click="navigate">
          <i class="fas fa-arrow-left icon-space" />
          {{ 'admin_to_overview' | translate }}
        </div>
      </router-link>
    </div>
    <MdtLoader v-if="loading" />
    <template v-else>
      <div class="object-title">
        {{ object.title }}
      </div>
      <div class="container">
        <div class="container-left">
          <slick :options="sliderOptions">
            <img
              v-for="(url, i) in object.images"
              :key="i"
              :src="url">
          </slick>
          <div
            v-if="object.virtual_tour_link"
            class="flex-center-h">
            <a
              :href="object.virtual_tour_link"
              target="_blank"
              rel="noopener noreferrer">
              <div class="btn btn-bordered basic">
                {{ 'admin_link_virtual_360' | translate }}
              </div>
            </a>
          </div>
          <div class="object-description">
            <div class="object-address">
              <i class="fas fa-map-marker-alt" />
              {{ object.building_adress }}
            </div>
            <div class="description-title">
              {{ object.description.displayName }}
            </div>
            <div
              class="description-value"
              v-html="sanitizedContent(object.description.value)" />
          </div>
          <div
            v-if="object.documents && object.documents.value && object.documents.value.length"
            class="object-documents">
            <MdtFileDownload
              v-for="(doc, i) in object.documents.value"
              :key="i"
              :name="doc.displayName"
              :path="doc.value" />
          </div>
        </div>
        <div class="container-right">
          <div class="object-details">
            <div class="object-title">
              {{ object.title }}
            </div>
            <div class="object-address">
              <i class="fas fa-map-marker-alt" />
              {{ object.building_adress }}
            </div>
            <div
              v-for="(section, i) in rightPanelFiltered"
              :key="i"
              class="section">
              <div class="section-title">
                {{ section.displayName }}
              </div>
              <div class="section-data">
                <div
                  v-for="(data, j) in section.value"
                  :key="j">
                  <p>{{ data.displayName }}</p>
                  <p>
                    <i
                      v-if="data.value === true"
                      class="fas fa-check" />
                    <span
                      v-else
                      v-html="sanitizedContent(data.value)" />
                  </p>
                </div>
              </div>
            </div>
            <div class="flex-center-h">
              <div
                v-if="requestSent"
                class="request-sent">
                {{ 'admin_request_has_been_sent' | translate }}
              </div>
              <div
                v-else-if="object.button.type === 'inside-form'"
                class="btn btn-primary btn-inside-form"
                @click="isModalOpen = true">
                {{ object.button.displayName }}
              </div>
              <a
                v-else
                :href="object.button.link"
                target="_blank"
                rel="noopener noreferrer">
                <div class="btn btn-primary">
                  {{ object.button.displayName }}
                </div>
              </a>
            </div>
          </div>
          <iframe
            id="gmap_canvas"
            title="public-object-details-map"
            class="google-map"
            :src="`https://maps.google.com/maps?q=${buildingAddressEncoded}&ie=UTF8&iwloc=&output=embed`"
            scrolling="no"
            frameborder="0" />
        </div>
      </div>
    </template>
    <ModalPublicContactForm
      v-if="isModalOpen"
      :salutation-choices="object.salutation"
      @close="isModalOpen = false"
      @requestSent="requestSent = true" />
  </div>
</template>

<script>
import cssVars from 'css-vars-ponyfill';
import { helpers, color } from '@/utility';
import ModalPublicContactForm from './ModalPublicContactForm.vue';

export default {
  name: 'PublicObjectDetails',
  components: {
    ModalPublicContactForm,
  },
  data() {
    return {
      object: {},
      isModalOpen: false,
      requestSent: false,
      loading: true,
      sliderOptions: {
        mobileFirst: true,
        arrows: false,
        dots: true,
        autoplay: true,
        autoplaySpeed: 2000,
      },
    };
  },
  computed: {
    buildingAddressEncoded() {
      return encodeURI(this.object.building_adress);
    },
    rightPanelFiltered() {
      return this.object.rightPanel.filter((section) => section.value.length);
    },
    settings() {
      return this.$store.state.publicPage;
    },
    settingsSet() {
      return this.$store.state.publicObjectList.settingsSet;
    },
  },
  mounted() {
    if (!this.settingsSet) {
      cssVars({
        variables: {
          '--color-back-primary': this.settings.iframe_background_color,
          '--color-back-primary-rgb': color.hexToRgb(this.settings.iframe_background_color),
          '--object-infobox-back-color': this.settings.iframe_second_background_color,
          '--color-theme': this.settings.iframe_theme_color,
          '--color-theme-rgb': color.hexToRgb(this.settings.iframe_theme_color),
          '--color-text-primary': this.settings.iframe_font_color,
          '--color-text-primary-rgb': color.hexToRgb(this.settings.iframe_font_color),
          '--color-text-secondary': this.settings.iframe_second_font_color,
          '--color-text-secondary-rgb': color.hexToRgb(this.settings.iframe_second_font_color),
        },
      });
      helpers.setFontPublic(this.settings.iframe_font, this.$store.getters.baseBackendUrl);

      this.$store.dispatch('publicObjectList/settingsAreSet');
    }

    this.$store.dispatch('publicObjectList/getObjectDetails', Number(this.$route.params.id)).then((data) => {
      this.object = data;
      this.loading = false;
    }).catch((err) => {
      this.showResponseError(err);
      switch (err.response.status) {
        case 404: {
          const notFoundRoute = this.$router.resolve({ name: 'NotFoundPublic' });
          window.open(`${window.location.origin}${notFoundRoute.href}`, '_self');
          break;
        }
        default: break;
      }
    });
  },
};
</script>

<style lang="scss" scoped>
.public-object-details {
  min-height: 100vh;
  padding: 22px;
  background-color: $color-back-primary;
  color: $color-text-primary;

  .back-button-row {
    margin-bottom: 18px;

    i {
      font-size: 16px;
    }
  }

  .object-title {
    font-size: 18px;
    margin-bottom: 10px;
  }

  .container {
    font-size: 14px;

    .slick-slider {
      margin-bottom: 32px;

      ::v-deep .slick-slide > div {
        height: 56.5vw;

        img {
          height: 100%;
          object-fit: contain;
          border-radius: 8px;
        }
      }
    }

    .object-address {
      color: $color-text-secondary;

      i {
        margin-right: 5px;
        font-size: 12px;
      }
    }

    .object-description {
      padding-top: 20px;
      margin-bottom: 24px;

      .object-address {
        margin-bottom: 12px;
      }

      .description-title {
        display: none;
        font-size: 20px;
        margin-bottom: 15px;
      }

      .description-value {
        @include ckeditor-preview;
      }
    }

    .object-documents {
      margin-bottom: 24px;
    }

    .object-details {
      margin-bottom: 24px;

      .object-title {
        display: none;
        margin-bottom: 20px;
        font-size: 28px;
        font-weight: bold;
      }

      .object-address {
        display: none;
        margin-bottom: 35px;
      }

      .section {
        margin-bottom: 24px;

        .section-title {
          margin-bottom: 8px;
          font-size: 16px;
          font-weight: bold;
        }

        .section-data > div {
          display: flex;
          justify-content: space-between;
          line-height: 1.7;

          p:last-child {
            max-width: 50%;
            text-align: right;
            font-weight: bold;
          }
        }
      }

      .request-sent {
        height: 40px;
        line-height: 40px;
        font-weight: bold;
      }
    }

    .google-map {
      width: 100%;
      height: 60vw;
    }
  }

  @media only screen and (min-width: 1280px) {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 50px;

    .back-button-row {
      width: 1180px; // 600px left + 30px margin + 550px right
      margin-bottom: 50px;
    }

    .object-title {
      display: none;
    }

    .container {
      display: flex;
      flex-direction: row;
      justify-content: center;
      font-size: 16px;

      .container-left {
        width: 600px;
        margin-right: 30px;

        .slick-slider {
          margin-bottom: 12px;

          ::v-deep {
            .slick-dots {
              bottom: 6px;
            }

            .slick-slide > div {
              height: 398px;
            }
          }
        }

        .object-description {
          .object-address {
            display: none;
          }

          .description-title {
            display: block;
          }
        }
      }

      .container-right {
        width: 550px;

        .object-details {
          padding: 40px;
          border-radius: 8px;
          background-color: var(--object-infobox-back-color);

          .object-title,
          .object-address {
            display: block;
          }

          .object-address i {
            margin-right: 12px;
            font-size: 20px;
          }

          .section {
            .section-title {
              font-size: 18px;
            }
          }
        }

        .google-map {
          height: 362px;
        }
      }
    }
  }

  @media only screen and (min-width: 1920px) {
    .back-button-row {
      width: 1475px; // 800px left + 50px margin + 625px right
    }

    .container {
      .container-left {
        width: 800px;
        margin-right: 50px;

        .slick-slider ::v-deep .slick-slide > div {
          height: 533px;
        }
      }

      .container-right {
        width: 625px;

        .object-details {
          padding: 50px 70px;
        }

        .google-map {
          height: 412px;
        }
      }
    }
  }
}
</style>
